import { useState, useEffect } from "react";
import swal from "sweetalert";

import { Grid, Button, Fab, Tooltip, Typography, Collapse, Box } from "@material-ui/core";
import { CloseOutlined, GetApp, CloudUploadOutlined } from "@material-ui/icons";

import ProgressDeterminate from '../../ProgressDeterminate';
import SimpleLoading from '../../SimpleLoading';
import CorruptedDocumentsModal from "../../solicitations/corrupted.documents.modal";
import DocumentPreview from "../../solicitations/documents/document.preview.component";
import RequestDocumentList from "./request.document.list.component";
import RequestDocumentsServiceConfigModal from "./request.documents.service.config.modal.component";
import RequestDocumentTypeConfirmationModal, { getVisibleDocumentTypeOptions } from "./request.documents.type.confirmation.modal.component";
import ContradictsResolutionModal from "./contradicts.resolution.modal.component";
import requestServiceDocumentService from "../../../domain/services/request/service/request.service.document.service";
import InvalidDocumentModal from './invalid.document.modal.component';

import documentsService from "../../../domain/services/solicitation/documents.service";
import loginService from "../../../domain/services/login.service";
import solicitationService from "../../../domain/services/solicitation/solicitation.service";
import useUploadOutcome from "../../../hooks/upload/useUploadOutcome";

import communicationProvider from "../../../providers/communication.provider";
import { stepsSolicitation } from "../../../domain/constant/steps.solicitation.constant";
import modalities from "../../../domain/constant/modalities.constant";
import { processDocumentAttachmentOptions } from "../../../domain/constant/request.documents.constant";
import { Inbox } from "react-feather";
import { requestServices } from "../../../domain/constant/request.services.constant";
import { selectedDownloadableDocuments } from '../../../domain/services/solicitation/document.expiration';
import supportContactService from '../../../domain/services/support.contact.service';
import clientLogger from '../../../domain/services/logging/client.logger';
import useReloadAfterFailure from '../../../hooks/useReloadAfterFailure';
import { loadDocuments } from './documents.load.recovery';
import { uploadOutcomeChannel } from '../../../domain/services/upload/upload.outcome.channel';
import { uploadRowOf, patchOfState, patchOfSettled, preparationFailed, patchRowsOf, withoutArrived, sentOf, isUploadRow } from '../../../domain/services/upload/upload.rows';

// Espelha o gate do backend (`validator.constants.js:CONTRADICTS_BLOCK_THRESHOLD`):
// confidence ≥ 94 bloqueia conclusão por divergência semântica de partes/conteúdo.
const CONTRADICTS_BLOCK_THRESHOLD = 94;
const PASS_THROUGH_KINDS = new Set(['jurisprudences']);

// Chave usada pra detectar tentativa de subir múltiplos docs principais do
// mesmo serviço no mesmo batch (backend rejeita ≥2 mains com mesmo service_id).
// `'__no_service'` cobre o caso em que nem o doc nem o request têm serviço declarado.
const resolveServiceKeyForMainDoc = ({ serviceId, request }) =>
    serviceId || request.serviceId || '__no_service';

export const buildClassificationDecision = ({
    classification,
    request,
    documents,
    availableServices,
}) => {
    const safeClassification = classification || null;
    const verdict = safeClassification && safeClassification.contentMatchVerdict;
    const confidence = (safeClassification && safeClassification.contentMatchConfidence) || 0;
    const documentKind = safeClassification && safeClassification.documentKind;
    const isAiMain = !!(safeClassification && safeClassification.role === 'main');
    const isPassThrough = PASS_THROUGH_KINDS.has(documentKind);
    const isUnsupportedFormat = verdict === 'unsupported_format';

    const isContradicts = !isPassThrough
        && verdict === 'contradicts'
        && confidence >= CONTRADICTS_BLOCK_THRESHOLD;

    const currentServiceKey = (availableServices || []).find(s => s.value === request.serviceId)?.key;
    const inferredKey = safeClassification && safeClassification.inferredServiceKey;
    const serviceMatchConfidence = (safeClassification && safeClassification.serviceMatchConfidence) || 0;
    const isServiceMismatch = !isContradicts
        && !isPassThrough
        && currentServiceKey
        && inferredKey
        && inferredKey !== 'unknown'
        && inferredKey !== currentServiceKey
        && serviceMatchConfidence >= CONTRADICTS_BLOCK_THRESHOLD;

    const classifyIncomplete = !safeClassification || !safeClassification.role;

    const inferredServiceId = safeClassification && safeClassification.inferredServiceKey
        ? ((availableServices || []).find(s => s.key === safeClassification.inferredServiceKey) || {}).value
        : null;
    const targetServiceId = inferredServiceId || request.serviceId || null;
    const alreadyHasMainForService = (documents || []).some(d =>
        d.isServiceMainDocument
        && !d.deleted
        && (!targetServiceId || d.serviceId === targetServiceId)
    );

    const aiSuggestedMain = isAiMain && !isUnsupportedFormat;
    let role = (aiSuggestedMain && !alreadyHasMainForService) ? 'main' : 'complementary';
    if (classifyIncomplete)
        role = 'complementary';

    const isMain = role === 'main';
    const serviceFromKey = safeClassification && safeClassification.inferredServiceKey
        ? (availableServices || []).find(s => s.key === safeClassification.inferredServiceKey)
        : null;
    let serviceId;
    if (classifyIncomplete) {
        serviceId = null;
    } else if (isMain) {
        serviceId = (serviceFromKey && serviceFromKey.value) || request.serviceId || null;
    } else {
        serviceId = null;
    }

    return {
        isContradicts,
        isServiceMismatch,
        isUnsupportedFormat,
        classifyIncomplete,
        role,
        serviceId,
        serviceFromKey,
        targetServiceId,
        alreadyHasMainForService,
        inferredKey,
    };
};

const RequestDocumentForm = ({ request, setRequest, resolvePending, setAlert, isEdit, isMainDocumentForm, refreshDocuments }) => {

    const [documents, setDocuments] = useState([]);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const [uploadList, setUploadList] = useState([]);

    const [preview, setPreview] = useState({ show: false });
    const [isFilesErrorModalOpen, setIsFilesErrorModalOpen] = useState(false);
    const [serviceConfigModal, setServiceConfigModal] = useState({});

    const [pendingDocs, setPendingDocs] = useState([]);
    const [contradictsModal, setContradictsModal] = useState({ open: false, blockedItems: [] });
    const [pendingFinalize, setPendingFinalize] = useState(null);

    const [isDocumentTypeModalOpen, setIsDocumentTypeModalOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const scheduleReload = useReloadAfterFailure();

    const [showInvalidDocumentModal, setShowInvalidDocumentModal] = useState(false);

    const reportUpload = useUploadOutcome(request.id, (outcome) => applyUploadOutcome(outcome));

    const alertUpload = (message, severity) => reportUpload({ message, severity });

    const canUpload = solicitationService.canUpload(request) || !isEdit;

    const hasSelectedDocs = !!documents.filter(item => item.checked).length;

    const acceptTypeFiles = [
        ".txt", ".doc", ".docx", ".csv", ".pjc", ".xls", ".xlsx", ".ppt", ".pptx",
        ".jpg", ".jpeg", ".png", ".eml", ".msg", ".html", ".pdf", ".mp3", ".mp4",
        ".mov", ".m4v", ".avi", ".mpeg", ".mpga", ".m4a", ".wav", ".webm", ".ogg", ".opus", ".asf"
    ];

    const convertibleTypeFiles = [".heic", ".heif"];

    const selectableTypeFiles = [...acceptTypeFiles, ...convertibleTypeFiles];

    const acceptTypeFilesToProcess = ["doc", "docx", "pdf"]

    useEffect(() => getAll(request.id), [request.id, refreshDocuments]);

    useEffect(() => {

        let isSubscribed = true

        if (isSubscribed)
            loadUploadList(documentsService.uploadListByAreaId(request.areaId));

        return () => { isSubscribed = false }

    }, [request.areaId, request.modalityId]);

    const getAll = (requestId, { quiet = false } = {}) =>
        loadDocuments({
            requestId,
            find: documentsService.find,
            onLoaded: showDocuments,
            setLoading: quiet ? () => undefined : setLoading,
            scheduleReload: () => scheduleReload(() => getAll(requestId)),
            logFailure: (error) => clientLogger.logClientError({ context: 'request:documents:load', message: 'leitura dos anexos falhou', payload: { requestId, status: error?.status ?? null }, error }),
        });

    const showDocuments = (data) => {

        const docs = data.filter((doc) => !doc.fieldId);

        setDocuments(docs);
        setPendingDocs(withoutArrived(docs));

        loadUploadList(
            documentsService
                .uploadListByAreaId(request.areaId)
                .filter(item => docs.length === 0 || !docs.some(doc => doc.nameType === item.nameType))
        );
    }

    const loadUploadList = (value) => {

        if (request.areaId && request.modalityId == 4) {
            setUploadList(value);
            return;
        }

        setUploadList([]);
    }

    const setProcessMainDocument = (selectedFile) => Object.assign(selectedFile, { isProcessMainDocument: true, isVisibleCustomer: true })

    const saveActionByKey = {
        'serviceDocument': (selectedFiles) => {
            inlineUploadFlow(selectedFiles);
            return { selectedFiles, isToReturn: true };
        },
        'processMainDocument': (selectedFiles) => {
            selectedFiles = selectedFiles.map(setProcessMainDocument);
            setRequest({ ...request, hasProcessDocument: true, processDocumentAttachment: processDocumentAttachmentOptions.ATTACHED });
            return { selectedFiles, isToReturn: false };
        }
    }

    const modalityRequiresProcessDocument = () => modalities[request.modalityKey]?.processDocumentRequired;

    const showDocumentTypesSelection = (docs) => !!(!splitDocuments.mainProcessDocument(docs).length && (modalityRequiresProcessDocument() || request.processNumber) && (loginService.isFaciliter() || showDocumentsOptionsToCustomer()));

    const shouldChargeBackExtraCredits = (key) => !!(key === 'processMainDocument' && !loginService.isFaciliter() && isEdit);

    const handleSave = (files, key = loginService.isFaciliter() && 'serviceDocument') => {

        let selectedFiles = [...Array.from(files)].reduce(validateFile, []);

        if (!selectedFiles.length) {
            return swal({
                title: "Selecione documentos válidos para o anexo",
                text: `Apenas arquivos com as extensões ${selectableTypeFiles.join(", ")} são permitidos.`,
                icon: "info"
            })
        }

        if (documentsService.isInCorrection(request))
            selectedFiles.map(file => ({ ...file, forCorrection: true }));

        if (key) {

            const results = saveActionByKey[key](selectedFiles);

            if (results.isToReturn)
                return;

            selectedFiles = results.selectedFiles
        }

        if (!loginService.isCustomer())
            selectedFiles.map(file => ({ ...file, isVisibleCustomer: true }));

        uploadEachFlow(selectedFiles, shouldChargeBackExtraCredits(key));
    }

    const uploadEachFlow = async (selectedFiles, showProcessDocumentMessage) => {
        setPendingDocs(previous => [...previous, ...selectedFiles.map(uploadRowOf)]);
        const patchRow = (file, patch) => patch && setPendingDocs(patchRowsOf([file], patch));
        const outcomes = await documentsService.saveEach(request.id, selectedFiles, {
            onFileState: (file, state) => patchRow(file, patchOfState(state)),
            onFileSettled: (file, settled) => {
                patchRow(file, patchOfSettled(settled));
                if (settled.status !== 'completed') uploadOutcomeChannel.deliver(request.id, { refused: true });
            },
        }).catch(error => {
            clientLogger.logClientError({ context: 'request:documents:upload', message: 'preparo do envio falhou', payload: { requestId: request.id }, error });
            setPendingDocs(patchRowsOf(selectedFiles, preparationFailed()));
            uploadOutcomeChannel.deliver(request.id, { refused: true });
            return [];
        });
        const sent = sentOf(selectedFiles, outcomes);
        if (!sent.length) return;
        await checkUpload(request.id, sent.map(({ outcome }) => ({ document: outcome.document })), sent.map(({ file }) => file), null, showProcessDocumentMessage);
        reportUpload({ reload: true, quiet: true });
    }

    const removePendingRow = (localId) => setPendingDocs(previous => previous.filter(row => row._localId !== localId));

    const saveDocuments = async (requestId, files, names, showProcessDocumentMessage = false) =>
        documentsService
            .saveAll(requestId, files)
            .then(res => checkUpload(requestId, res, files, names, showProcessDocumentMessage))
            .then(() => reportUpload({ reload: true }))
            .catch(err => {
                if (err?.response?.data?.invalidDocumentContent) {
                    reportUpload({ invalidContent: true });
                    return;
                }

                return communicationProvider.getErrorMessage(err, alertUpload, "Erro ao salvar! Contate o atendimento.")
            })
            .finally(() => setLoading(false));

    const checkUpload = async (requestId, response, files, names, showProcessDocumentMessage = false) => {

        const documentIds = (response || []).map(item => item.document.id);

        if (!documentIds.length)
            return;

        await documentsService
            .checkUpload(requestId, documentIds, false)
            .then(() => reportUpload({
                message: showProcessDocumentMessage ? 'Processo anexado com sucesso!' : 'Upload concluído com sucesso!',
                severity: "success",
                pendingCheck: files,
            }))
            .catch(() => reportUpload({ filesError: true }));

        if ((names || []).length > 0)
            await Promise.all(documentIds.map((id, index) => update(id, { name: names[index] }, true, true)));
    }

    const validateFile = (files, input) => {

        var fileExtension = input.name.split('.').pop().toLowerCase();

        if (selectableTypeFiles.includes(`.${fileExtension}`)) {
            files.push(input);
            return files;
        }

        return files;
    }

    const onConfirmServiceConfig = async (documents) => {

        setLoading(true);

        const updatedDocuments = [];
        for (const { document, data } of documents) {
            if (document.id) {
                await update(document.id, data);
                updatedDocuments.push(document.id);
                return
            }

            for (const element of Object.keys(data).filter(key => key !== 'name'))
                document[element] = data[element]
        }

        const documentsToSave = documents.filter(doc => !updatedDocuments.includes(doc.id));
        saveDocuments(request.id, documentsToSave.map(({ document }) => document), documentsToSave.map(({ data }) => data.name));

        handleCloseConfig();
    }

    const update = async (documentId, serviceConfig, noFeedback, throwError = false) => {

        const notNullData = Object.entries(serviceConfig).reduce((obj, [key, value]) => {
            if (value === null)
                return obj;
            return { ...obj, [key]: value };
        }, {});

        return documentsService
            .update({ ...notNullData, id: documentId, requestId: request.id })
            .then(() => {
                !noFeedback && setAlert('Documento editado com sucesso!', 'success');
                getAll(request.id);
                handleCloseConfig();
            })
            .catch(err => {
                if (throwError)
                    throw err;
                communicationProvider.getErrorMessage(err, setAlert)
            })
            .finally(() => setLoading(false));
    }

    const persistProcessMainFlag = (doc, isProcessMainDocument) =>
        documentsService.update({ id: doc.id, requestId: request.id, isProcessMainDocument });

    // Troca (De→Para) do documento do processo a partir da visão interna.
    // A confirmação nomeia o documento atual e o novo; o anterior é apenas
    // desmarcado (não excluído). O backend também garante um único processo.
    const handleSetProcessDocument = async (document) => {
        const current = documents.find(doc => doc.isProcessMainDocument && doc.id !== document.id);

        const confirmed = await swal({
            title: current ? 'Trocar o documento do processo?' : 'Definir como documento do processo?',
            text: current
                ? `De: ${current.displayName || current.name}\nPara: ${document.displayName || document.name}\n\nO atual deixa de ser o principal (não é excluído). Só pode haver um documento do processo por solicitação.`
                : `Marcar "${document.displayName || document.name}" como o documento do processo desta solicitação.`,
            icon: 'warning',
            buttons: { confirm: { text: current ? 'Trocar' : 'Definir', className: 'MuiButton-containedPrimary' }, cancel: 'Cancelar' },
        });

        if (!confirmed)
            return;

        try {
            if (current)
                await persistProcessMainFlag(current, false);
            await persistProcessMainFlag(document, true);
            setAlert('Documento do processo selecionado com sucesso!', 'success');
            getAll(request.id);
        } catch (err) {
            communicationProvider.getErrorMessage(err, setAlert);
        }
    };

    const handleUnsetProcessDocument = async (document) => {
        try {
            await persistProcessMainFlag(document, false);
            setAlert('Documento do processo desmarcado com sucesso!', 'success');
            getAll(request.id);
        } catch (err) {
            communicationProvider.getErrorMessage(err, setAlert);
        }
    };

    const checkIsPending = (newFiles = []) => {

        if (request.status != stepsSolicitation.PENDING || !loginService.isCustomer() || request.isFinancialPending)
            return;

        if (!documentsService.hasPending(request, [...documents, ...newFiles]))
            swal({
                title: 'Resolver Pendências',
                text: `
                    Precisamos da sua colaboração para resolver pendências nesta solicitação.

                    Por favor, verifique se você já anexou os comentários necessários e/ou inseriu as informações solicitadas pelo analista. Sua pronta resposta é fundamental para prosseguirmos.

                    Você já tratou todas as pendências existentes?

                    Agradecemos sua ajuda!
                `,
                icon: 'info',
                buttons: { confirm: { text: 'Sim', className: 'MuiButton-containedPrimary' }, cancel: 'Não' },
                closeOnClickOutside: false,
            }).then(res => (res) && resolvePending(request.id));
    }

    const selectAll = (checked) => {
        let item = [...documents].map(item => ({ ...item, checked }));
        setDocuments(item);
    }

    const selectFile = (index, checked) => {
        let item = [...documents];
        item[index].checked = checked;
        setDocuments(item);
    }

    const downloadAll = async () => {
        const selectedDocuments = selectedDownloadableDocuments(documents);

        if (selectedDocuments.length === 0) {
            return swal({
                title: "Nenhum documento selecionado",
                text: "Selecione pelo menos um documento para fazer o download.",
                icon: "info"
            });
        }

        if (selectedDocuments.length === 1)
            return documentsService.downloadFile(selectedDocuments[0].url, selectedDocuments[0].name);

        if (isDownloadingAll) return;

        try {
            setIsDownloadingAll(true);
            await documentsService.downloadAllAsZip(selectedDocuments, `documentos_solicitacao_${request.id}.zip`);
            setAlert('Download concluído com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao criar ZIP:', error);
            setAlert('Erro ao preparar download. Tente novamente.', 'error');
        } finally {
            setIsDownloadingAll(false);
        }
    };

    const visibility = async (file, show) => setPreview({ ...file, show });

    const showRemoveConfimation = ({ title, message, doc, index, needExtraCharge = false }) =>
        swal({
            title,
            text: `Realmente deseja remover o documento? ${message}`,
            icon: 'warning',
            buttons: { confirm: { text: 'Remover', className: 'MuiButton-containedPrimary' }, cancel: 'Cancelar' }
        }).then((res) => (res) && removeDocument(doc, index, false))

    const splitDocuments = {
        'mainProcessDocument': (docs) => docs.filter(doc => !!doc.isProcessMainDocument),
        'defaultDocuments': (docs) => isEdit ? docs : docs.filter(doc => !doc.isProcessMainDocument)
    }

    const removeDocument = async (doc, index, checkDocRequired = true) => {


        if (checkDocRequired && documentsService.showRequiredDocumentConfirmation(request, doc)) {

            showRemoveConfimation({
                title: 'Remoção de Documento Obrigatório',
                message: 'A solicitação será retornada para pendência!',
                doc, index
            })

            return;
        }

        if (checkDocRequired && documentsService.showServiceMainDocumentConfirmation(request, doc)) {

            showRemoveConfimation({
                title: 'Remoção de Documento Principal',
                message: 'Esse documento está sendo mostrado para o cliente.',
                doc, index
            })

            return;
        }

        if (checkDocRequired && doc.isProcessMainDocument && isEdit) {

            showRemoveConfimation({
                title: 'Remoção do processo',
                message: `Esse documento é obrigatório, caso seja removido, ${loginService.isCustomer() ? 'vamos buscar por você por um valor de R$20' : 'vamos cobrar R$20 do pacote do cliente'}`,
                doc, index, needExtraCharge: true
            })

            return;
        }

        if (doc.isProcessMainDocument)
            setRequest({ ...request, hasProcessDocument: false, processDocumentAttachment: processDocumentAttachmentOptions.NOT_ATTACHED })

        if (doc.id)
            await documentsService.remove(doc);

        removeInList(index);

        if (documentsService.isDocumentRequired(request, doc))
            getAll(request.id);
    }

    const removeInList = (docIndex) => {
        let item = [...documents];
        item.splice(docIndex, 1);
        setDocuments([...item]);
    }

    const handleOpenConfig = (documents, isEdit) =>
        setServiceConfigModal({ open: true, documents, isEdit });

    const handleCloseConfig = () =>
        setServiceConfigModal({ open: false });

    const executeFinalize = async (items, blocked = []) => {
        if (items.length > 0)
            await documentsService.finalizeStaging(request.id, items);

        const finalizedStagingIds = new Set(items.map(i => i.stagingId));

        setPendingDocs(prev => prev.filter(p => !p.stagingId || !finalizedStagingIds.has(p.stagingId)));

        if (blocked.length > 0) {
            setPendingDocs(prev => prev.filter(p => !blocked.some(b => b.stagingId === p.stagingId)));
            setContradictsModal({ open: true, blockedItems: blocked });
        }

        await reportUpload({
            reload: true,
            ...(items.length > 0 ? { message: `${items.length === 1 ? 'Documento enviado' : 'Documentos enviados'} com sucesso!`, severity: 'success' } : {}),
        });
    };

    const openContradicts = ({ pendingFinalize: waitingFinalize, blockedItems }) => {
        setPendingFinalize(waitingFinalize);
        setContradictsModal({ open: true, blockedItems });
    };

    const applyUploadOutcome = (outcome) => {
        if (outcome.message) setAlert(outcome.message, outcome.severity);
        if (outcome.pendingCheck) checkIsPending(outcome.pendingCheck);
        if (outcome.filesError) setIsFilesErrorModalOpen(true);
        if (outcome.invalidContent) setShowInvalidDocumentModal(true);
        if (outcome.contradicts) openContradicts(outcome.contradicts);
        return outcome.reload ? getAll(request.id, { quiet: Boolean(outcome.quiet) }) : undefined;
    };

    const inlineUploadFlow = async (selectedFiles) => {

        if (!selectedFiles || selectedFiles.length === 0) return;

        const availableServices = await requestServiceDocumentService
            .getRequestServices(request.mainId || request.id)
            .then(list => (list || []).filter(s => s.value))
            .catch(() => []);

        const placeholders = selectedFiles.map(file => ({
            _localId: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            _isPending: true,
            file,
            name: file.name,
            originalUploadName: file.name,
            fileSize: file.size,
            fileType: file.type,
            status: 'uploading',
            classification: null,
        }));

        setPendingDocs(prev => [...prev, ...placeholders]);

        const handleUploadProgress = (file, percent) => {
            setPendingDocs(prev => prev.map(p => (
                p.file === file ? { ...p, uploadProgress: percent } : p
            )));
        };

        try {
            const stagingsResp = await documentsService.saveAllStaging(request.id, selectedFiles, handleUploadProgress);
            const stagings = (stagingsResp || []).filter(s => s && s.stagingId);

            if (stagings.length === 0)
                throw new Error('Resposta de upload vazia.');

            setPendingDocs(prev => prev.map(p => {
                const idx = selectedFiles.indexOf(p.file);
                if (idx >= 0 && stagings[idx]) {
                    return {
                        ...p,
                        stagingId: stagings[idx].stagingId,
                        url: stagings[idx].url,
                        bucketName: stagings[idx].bucketName,
                        status: 'analyzing',
                        analyzingStartedAt: Date.now(),
                    };
                }
                return p;
            }));

            const newStagingIds = stagings.map(s => s.stagingId);
            const classifyResult = await documentsService.classifyStaging(request.id, newStagingIds);

            const classifyItems = (classifyResult && Array.isArray(classifyResult.items) ? classifyResult.items : [])
                .filter(c => c && c.stagingId != null);
            const classifMap = new Map(classifyItems.map(c => [Number(c.stagingId), c.classification]));

            const blocked = [];
            const finalizeItems = [];
            const mainTakenByService = new Set();

            for (const staging of stagings) {
                const classification = classifMap.get(Number(staging.stagingId));
                const decision = buildClassificationDecision({
                    classification,
                    request,
                    documents,
                    availableServices,
                });

                if (decision.classifyIncomplete) {
                    // eslint-disable-next-line no-console
                    console.warn('[inlineUploadFlow] classify retornou sem role — degradando pra complementary', {
                        stagingId: staging.stagingId,
                    });
                }

                if (decision.isContradicts || decision.isServiceMismatch) {
                    let blockedClassification = classification;
                    if (decision.isServiceMismatch) {
                        const inferredService = availableServices.find(s => s.key === decision.inferredKey);
                        const currentService = availableServices.find(s => s.value === request.serviceId);
                        const inferredLabel = (inferredService && inferredService.name) || decision.inferredKey;
                        const currentLabel = (currentService && currentService.name) || 'do serviço atual';
                        blockedClassification = {
                            ...classification,
                            justification: `Esse arquivo parece ser de ${inferredLabel}, mas o serviço em produção é ${currentLabel}. Suba o documento principal correto ou cancele este envio.`,
                        };
                    }
                    blocked.push({
                        stagingId: staging.stagingId,
                        name: staging.name,
                        fileType: staging.fileType,
                        fileSize: staging.fileSize,
                        url: staging.url,
                        bucketName: staging.bucketName,
                        originalUploadName: staging.name,
                        classification: blockedClassification,
                        status: 'ready',
                        role: 'complementary',
                    });
                } else {
                    let { role, serviceId } = decision;
                    if (role === 'main') {
                        const serviceKeyForMain = resolveServiceKeyForMainDoc({ serviceId, request });
                        if (mainTakenByService.has(serviceKeyForMain)) {
                            // Backend rejeita 2+ docs principais do mesmo serviço no mesmo batch;
                            // mantém apenas o 1º como main e degrada os demais pra complementary.
                            role = 'complementary';
                            serviceId = null;
                        } else {
                            mainTakenByService.add(serviceKeyForMain);
                        }
                    }
                    const isMain = role === 'main';

                    finalizeItems.push({
                        stagingId: staging.stagingId,
                        role,
                        serviceId,
                        forCorrection: isMain && documentsService.isInCorrection(request),
                        overridden: false,
                    });
                }
            }

            const requestAlreadyHasMain = documents.some(d =>
                d.isServiceMainDocument && !d.deleted
            );
            const blockedDuringClassify = blocked.length > 0;
            const hasMain = finalizeItems.some(i => i.role === 'main');
            const singleAutoPromote = finalizeItems.length === 1
                && !hasMain
                && !requestAlreadyHasMain
                && !blockedDuringClassify
                && finalizeItems[0].role !== 'complementary';

            if (singleAutoPromote) {
                const item = finalizeItems[0];
                item.role = 'main';
                item.serviceId = item.serviceId || request.serviceId || null;
            }

            if (blocked.length > 0) {
                const blockedIds = new Set(blocked.map(b => b.stagingId));
                setPendingDocs(prev => prev
                    .filter(p => !p.stagingId || !blockedIds.has(p.stagingId))
                    .map(p => (
                        finalizeItems.some(i => i.stagingId === p.stagingId)
                            ? { ...p, status: 'awaiting' }
                            : p
                    ))
                );
                reportUpload({ contradicts: { pendingFinalize: { items: finalizeItems }, blockedItems: blocked } });
                return;
            }

            await executeFinalize(finalizeItems, []);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[inlineUploadFlow] Falha:', err);
            setPendingDocs(prev => prev.filter(p => !selectedFiles.includes(p.file)));
            communicationProvider.getErrorMessage(err, alertUpload, 'Não conseguimos preparar os arquivos pra envio agora. Tente novamente.');
        }
    };

    const releasePendingFinalizeIfReady = async (newBlockedItems) => {
        if (newBlockedItems.length > 0) return;
        if (!pendingFinalize || !pendingFinalize.items || pendingFinalize.items.length === 0) return;
        const toFinalize = pendingFinalize.items;
        setPendingFinalize(null);
        try {
            await executeFinalize(toFinalize, []);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[releasePendingFinalize] Falha:', err);
            setAlert('Não conseguimos finalizar os documentos restantes. Tente novamente.', 'error');
            setPendingDocs(prev => prev.filter(p => !toFinalize.some(i => i.stagingId === p.stagingId)));
        }
    };

    const processSubstituteUpload = async (newFile) => {
        const availableServices = await requestServiceDocumentService
            .getRequestServices(request.mainId || request.id)
            .then(list => (list || []).filter(s => s.value))
            .catch(() => []);
        const stagings = await documentsService.saveAllStaging(request.id, [newFile]);
        if (!stagings || !stagings[0] || !stagings[0].stagingId) {
            return { success: false, error: 'upload_failed' };
        }
        const staging = stagings[0];

        const classifyResult = await documentsService.classifyStaging(request.id, [staging.stagingId]);
        const classifyItems = (classifyResult && Array.isArray(classifyResult.items) ? classifyResult.items : [])
            .filter(c => c && c.stagingId != null);
        const classification = (classifyItems[0] || {}).classification || {};
        const verdict = classification.contentMatchVerdict;
        const confidence = classification.contentMatchConfidence;
        const isAiMain = classification.role === 'main';

        if (isAiMain && verdict === 'contradicts' && (confidence || 0) >= CONTRADICTS_BLOCK_THRESHOLD) {
            return {
                success: false,
                blockedItem: {
                    stagingId: staging.stagingId,
                    name: staging.name,
                    fileType: staging.fileType,
                    fileSize: staging.fileSize,
                    url: staging.url,
                    bucketName: staging.bucketName,
                    originalUploadName: staging.name,
                    classification,
                    status: 'ready',
                    role: 'complementary',
                },
            };
        }

        const inferredServiceId = classification.inferredServiceKey
            ? (availableServices.find(s => s.key === classification.inferredServiceKey) || {}).value
            : null;
        const targetServiceId = inferredServiceId || request.serviceId || null;
        const alreadyHasMainForService = documents.some(d =>
            d.isServiceMainDocument
            && !d.deleted
            && (!targetServiceId || d.serviceId === targetServiceId)
        );
        const aiSuggestedMain = !!(classification && classification.role === 'main');
        let role = (aiSuggestedMain && !alreadyHasMainForService) ? 'main' : 'complementary';
        let serviceId = null;
        if (role === 'main') {
            serviceId = targetServiceId
                || (availableServices.length === 1 ? availableServices[0].value : null)
                || request.serviceId
                || null;
            if (!serviceId) role = 'complementary';
        }
        const isMain = role === 'main';
        const finalizeItem = {
            stagingId: staging.stagingId,
            role,
            serviceId,
            forCorrection: isMain && documentsService.isInCorrection(request),
            overridden: false,
        };
        await executeFinalize([finalizeItem], []);
        return { success: true };
    };

    const handleContradictsReplace = async (oldStagingId, newFile) => {
        try {
            await documentsService.removeStaging(request.id, oldStagingId);
        } catch (_e) { /* cron limpa em 24h se falhar */ }

        try {
            const result = await processSubstituteUpload(newFile);
            if (result.success) {
                return { success: true };
            }
            if (result.blockedItem) {
                const newBlockedItems = contradictsModal.blockedItems.map(i => (
                    i.stagingId === oldStagingId ? result.blockedItem : i
                ));
                setContradictsModal({ blockedItems: newBlockedItems, open: true });
                return { success: false };
            }
            setAlert('Não conseguimos processar o arquivo. Tente novamente.', 'error');
            return { success: false };
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[handleContradictsReplace] Falha:', err);
            setAlert('Não conseguimos processar o arquivo. Tente novamente.', 'error');
            return { success: false };
        }
    };

    const handleContradictsItemResolved = async (resolvedStagingId) => {
        const newBlockedItems = contradictsModal.blockedItems.filter(i => i.stagingId !== resolvedStagingId);
        setContradictsModal({
            blockedItems: newBlockedItems,
            open: newBlockedItems.length > 0,
        });
        await releasePendingFinalizeIfReady(newBlockedItems);
    };

    const handleContradictsCancel = async (stagingId) => {
        try {
            await documentsService.removeStaging(request.id, stagingId);
        } catch (_e) { /* cron limpa em 24h se falhar */ }
        const newBlockedItems = contradictsModal.blockedItems.filter(i => i.stagingId !== stagingId);
        setContradictsModal({
            blockedItems: newBlockedItems,
            open: newBlockedItems.length > 0,
        });
        await releasePendingFinalizeIfReady(newBlockedItems);
    };

    const optionsSelectAll = () => {

        const iconsStyle = { marginRight: 10, minHeight: 30, height: 30, minWidth: 30, width: 30 }

        return (
            <Collapse orientation="horizontal" in={hasSelectedDocs}>
                <Grid item>

                    <Tooltip title="Download">
                        <Fab
                            type="button" color="primary"
                            style={iconsStyle} onClick={downloadAll} disabled={isDownloadingAll}
                        >
                            <GetApp fontSize="small" style={{ paddingTop: 3 }} />
                        </Fab>
                    </Tooltip>

                    <Tooltip title="Cancelar">
                        <Fab
                            type="button" color="default" style={iconsStyle}
                            onClick={() => selectAll(false)}
                        >
                            <CloseOutlined fontSize="small" style={{ paddingTop: 3 }} />
                        </Fab>
                    </Tooltip>

                </Grid>
            </Collapse>
        );
    }

    const addAttribute = (files, attribute, value) => {

        if (files && files.length > 0)
            files[0][attribute] = value

        return files
    }

    const UploadList = () =>
        uploadList.map(item => (
            isDocumentIncluded(item.nameType) ? null : (
                <div
                    key={item.nameType}
                    style={{
                        padding: '10px', marginTop: '10px', display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center',
                        border: `1px dotted #aaa`, borderRadius: '5px', width: '100%'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CloudUploadOutlined style={{ marginRight: '10px' }} />
                        <div>
                            <span style={{ marginRight: '10px', fontWeight: '600' }}>
                                {item.title}
                            </span>
                            <span style={item.required ? ({ background: '#F55353', color: '#FFF', padding: '3px', fontSize: '10px', borderRadius: '5px', fontWeight: '600' }) : ({ background: '#4D96FF', color: '#FFF', padding: '3px', fontSize: '10px', borderRadius: '5px', fontWeight: '600' })}>{item.required ? 'obrigatório' : 'opcional'}</span> </div>
                    </div>
                    <span>
                        <Button variant="contained" style={{ padding: 0 }} disabled={!canUpload}>
                            <label style={{ cursor: 'pointer', padding: '6px 12px 6px 12px' }} title={`Anexar ${item.title}`} htmlFor={item.nameType}>Anexar</label>
                        </Button>
                        <input
                            type="file"
                            id={item.nameType}
                            accept={acceptTypeFiles.join(",")}
                            style={{ display: 'none' }}
                            onChange={(e) =>
                                handleSave(addAttribute(e.target.files, 'nameType', item.nameType))
                            }
                        />
                    </span>
                </div>
            )
        ));

    const acceptTypeFilesByDocumentType = {
        'processMainDocument': () => acceptTypeFilesToProcess,
        'default': () => !loginService.isFaciliter() ? acceptTypeFiles : requestServices.structure[request.serviceKey]?.serviceDocumentAcceptTypeFiles || acceptTypeFiles
    }

    const getInput = ({ id, multiple, disabled, key }) => {

        const getAcceptTypeFiles = acceptTypeFilesByDocumentType[key] || acceptTypeFilesByDocumentType['default']
        const documentTypesAvailable = getAcceptTypeFiles()

        return (
            <input
                type="file" disabled={disabled}
                style={{ display: 'none' }} id={id}
                multiple={!isEdit || multiple}
                accept={documentTypesAvailable.join(",")}
                onChange={(e) => {
                    isDocumentTypeModalOpen && setIsDocumentTypeModalOpen(false);
                    handleSave(e.target.files, key);
                }}
            />
        )
    }

    const isDocumentIncluded = (nameType) => !!documents.find(doc => doc.nameType === nameType);

    const allowedStatusToCustomerUploadProcessMainDocument = [stepsSolicitation.ANALYSE, stepsSolicitation.LINK, stepsSolicitation.FROZEN, stepsSolicitation.PENDING];

    const showDocumentsOptionsToCustomer = () => loginService.isCustomer() && allowedStatusToCustomerUploadProcessMainDocument.includes(request.status);

    const shouldDisplayProcessMainDocumentType = (docs) => isEdit &&
        (!docs.find(doc => doc.isProcessMainDocument) && request.processDocumentRequired) &&
        (loginService.isFaciliter() || loginService.isInternal() || showDocumentsOptionsToCustomer());

    const visibleDocumentTypeOptions = getVisibleDocumentTypeOptions({
        loginService,
        shouldDisplayProcessMainDocumentType,
        documents,
        showDocumentsOptionsToCustomer,
    });

    const singleDocumentTypeOption = visibleDocumentTypeOptions.length === 1 ? visibleDocumentTypeOptions[0] : null;

    const getInternalDocumentTypeModal = () => {

        if ((!loginService.isInternal() && !showDocumentTypesSelection(documents)) || isMainDocumentForm || !isDocumentTypeModalOpen)
            return null;

        if (visibleDocumentTypeOptions.length <= 1)
            return null;

        return (
            <RequestDocumentTypeConfirmationModal
                getInput={getInput}
                handleClose={() => setIsDocumentTypeModalOpen(false)}
                loginService={loginService}
                documents={documents}
                shouldDisplayProcessMainDocumentType={shouldDisplayProcessMainDocumentType}
                showDocumentsOptionsToCustomer={showDocumentsOptionsToCustomer}
            />
        )
    }

    const getServiceConfigModal = () => {

        if (!serviceConfigModal.open)
            return null;

        return (
            <RequestDocumentsServiceConfigModal
                request={request}
                documents={serviceConfigModal.documents}
                onConfirm={onConfirmServiceConfig}
                handleClose={handleCloseConfig}
                isEdit={serviceConfigModal.isEdit}
            />
        )
    }

    const allowOnlyOneDocument = () => !!(documents.filter(doc => doc.isProcessMainDocument).length >= 1);

    const disabledButton = () => isMainDocumentForm ? allowOnlyOneDocument() : !canUpload

    const hideButton = () => !!((isMainDocumentForm && allowOnlyOneDocument()) || (isMainDocumentForm && isEdit));

    const handleContactSupport = () =>
        window.open(supportContactService.supportUrl(), "_blank");

    return (
        <>

            <Grid item xs={12} container style={{ padding: -15 }}>

                {loading && <SimpleLoading message="Carregando lista..." />}

                <ProgressDeterminate style={{ display: "block" }} />

                {!loading && (
                    <>

                        <Grid
                            container justifyContent="space-between" alignItems="end"
                            direction={!isMainDocumentForm && "row-reverse"} style={{ marginBottom: 15 }}
                        >
                            <div>

                                {getInput({
                                    id: isMainDocumentForm ? 'file' : 'files',
                                    disabled: !canUpload,
                                    multiple: true,
                                    key: isMainDocumentForm
                                        ? 'processMainDocument'
                                        : (singleDocumentTypeOption ? singleDocumentTypeOption.key : undefined),
                                })}

                                {!!documents.length && !isMainDocumentForm &&
                                    <Button
                                        className="hover:border-[#376fd0] hover:text-[#376fd0] hover:!bg-[transparent]"
                                        color="default"
                                        variant="outlined"
                                        style={{ marginRight: 10 }}
                                        onClick={() => selectAll(true)}
                                    >
                                        Selecionar todos
                                    </Button>
                                }

                                {!hideButton() && (
                                    <label htmlFor={(
                                        (!loginService.isInternal() && !showDocumentTypesSelection(documents))
                                        || isMainDocumentForm
                                        || (!isMainDocumentForm && !!singleDocumentTypeOption)
                                    ) && (isMainDocumentForm ? 'file' : 'files')}>
                                        <Button
                                            color="primary" component="span" startIcon={<CloudUploadOutlined />}
                                            disabled={disabledButton()} variant="contained"
                                            onClick={() => { if (!singleDocumentTypeOption) setIsDocumentTypeModalOpen(true); }}
                                        >
                                            Anexar {isMainDocumentForm ? 'processo' : 'documentos'}
                                        </Button>
                                    </label>
                                )}


                            </div>

                            {!!documents.length && hasSelectedDocs && (
                                <Grid>
                                    {optionsSelectAll()}
                                </Grid>
                            )}

                        </Grid>


                        {!documents.length && (
                            <div className="w-full flex flex-col justify-center items-center gap-4 text-center">
                                <Inbox className="text-gray-300" size={52} />
                                <span className="text-base text-gray-400">
                                    Nenhum documento anexado
                                    {request.status === stepsSolicitation.CONCLUDED && (
                                        <span className="max-w-xl">
                                            <br />Verifique os detalhes para encontrar os documentos principais da solicitação
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}

                        <RequestDocumentList
                            request={request}
                            documents={[
                                ...(!isMainDocumentForm ? splitDocuments.defaultDocuments(documents) : splitDocuments.mainProcessDocument(documents)),
                                ...(!isMainDocumentForm ? pendingDocs : pendingDocs.filter(isUploadRow)),
                            ]}
                            onRemovePending={removePendingRow}
                            selectFile={selectFile}
                            visibility={visibility}
                            handleOpenConfig={handleOpenConfig}
                            removeDocument={removeDocument}
                            update={update}
                            isEdit={isEdit}
                            setProcessMainDocument={handleSetProcessDocument}
                            unsetProcessMainDocument={handleUnsetProcessDocument}
                            pauseAllPending={contradictsModal.open}
                        />

                        <Grid item style={{ width: "100%" }}>
                            <UploadList />
                        </Grid>

                    </>
                )}

            </Grid>

            {preview.show && (
                <DocumentPreview file={preview} onSelect={visibility} />
            )}

            {isFilesErrorModalOpen && (
                <CorruptedDocumentsModal
                    isOpen={isFilesErrorModalOpen}
                    request={request}
                    alert={setAlert}
                    onClose={() => {
                        setIsFilesErrorModalOpen(false);
                        getAll(request.id);
                    }}
                    editCheckPending={checkIsPending}
                />
            )}

            {getServiceConfigModal()}

            <ContradictsResolutionModal
                open={contradictsModal.open}
                blockedItems={contradictsModal.blockedItems}
                onReplace={handleContradictsReplace}
                onCancel={handleContradictsCancel}
                onItemResolved={handleContradictsItemResolved}
            />

            {getInternalDocumentTypeModal()}

            <InvalidDocumentModal
                open={showInvalidDocumentModal}
                onClose={() => setShowInvalidDocumentModal(false)}
                onContactSupport={handleContactSupport}
            />

        </>
    );
}

export default RequestDocumentForm;